/**
 * OneSignal Web Push Notifications Integration
 *
 * This module provides a wrapper around OneSignal's Web Push SDK for more reliable
 * push notifications with better delivery, analytics, and management.
 */

import OneSignal from 'react-onesignal'

const ONESIGNAL_APP_ID = import.meta.env.VITE_ONESIGNAL_APP_ID || ''

let isInitialized = false

/**
 * Check if OneSignal is configured
 */
export function isOneSignalConfigured(): boolean {
  return Boolean(ONESIGNAL_APP_ID)
}

/**
 * Initialize OneSignal SDK
 * Should be called once on app startup
 */
export async function initOneSignal(): Promise<boolean> {
  if (isInitialized) {
    console.log('[OneSignal] Already initialized')
    return true
  }

  if (!isOneSignalConfigured()) {
    console.warn('[OneSignal] App ID not configured')
    return false
  }

  try {
    await OneSignal.init({
      appId: ONESIGNAL_APP_ID,
      allowLocalhostAsSecureOrigin: import.meta.env.DEV,
      serviceWorkerParam: {
        scope: '/',
      },
    })

    isInitialized = true
    console.log('[OneSignal] Initialized successfully')
    return true
  } catch (error) {
    console.error('[OneSignal] Initialization failed:', error)
    return false
  }
}

/**
 * Link OneSignal player to app user
 * This enables targeting users by their external user ID
 */
export async function setExternalUserId(userId: string): Promise<void> {
  if (!isInitialized) {
    console.warn('[OneSignal] Not initialized, cannot set external user ID')
    return
  }

  try {
    await OneSignal.login(userId)
    console.log('[OneSignal] External user ID set:', userId)
  } catch (error) {
    console.error('[OneSignal] Failed to set external user ID:', error)
  }
}

/**
 * Remove external user ID (on logout)
 */
export async function removeExternalUserId(): Promise<void> {
  if (!isInitialized) return

  try {
    await OneSignal.logout()
    console.log('[OneSignal] External user ID removed')
  } catch (error) {
    console.error('[OneSignal] Failed to remove external user ID:', error)
  }
}

/**
 * Set a tag on the user (for segmentation)
 */
export async function sendTag(key: string, value: string): Promise<void> {
  if (!isInitialized) return

  try {
    await OneSignal.User.addTag(key, value)
    console.log('[OneSignal] Tag set:', key, value)
  } catch (error) {
    console.error('[OneSignal] Failed to set tag:', error)
  }
}

/**
 * Set multiple tags at once
 */
export async function sendTags(tags: Record<string, string>): Promise<void> {
  if (!isInitialized) return

  try {
    await OneSignal.User.addTags(tags)
    console.log('[OneSignal] Tags set:', tags)
  } catch (error) {
    console.error('[OneSignal] Failed to set tags:', error)
  }
}

/**
 * Check if the user has opted in to push notifications
 */
export async function isPushEnabled(): Promise<boolean> {
  if (!isInitialized) return false

  try {
    return await OneSignal.Notifications.permission
  } catch (error) {
    console.error('[OneSignal] Failed to check push status:', error)
    return false
  }
}

/**
 * Prompt the user to opt-in to push notifications
 */
export async function optIn(): Promise<boolean> {
  if (!isInitialized) {
    console.warn('[OneSignal] Not initialized, cannot opt in')
    return false
  }

  try {
    // Request permission
    await OneSignal.Notifications.requestPermission()

    const isEnabled = await isPushEnabled()
    console.log('[OneSignal] Opt-in result:', isEnabled)
    return isEnabled
  } catch (error) {
    console.error('[OneSignal] Failed to opt in:', error)
    return false
  }
}

/**
 * Opt the user out of push notifications
 */
export async function optOut(): Promise<void> {
  if (!isInitialized) return

  try {
    // OneSignal doesn't have a direct opt-out method for web
    // The user needs to manage this through browser settings
    // But we can unsubscribe from pushes
    console.log('[OneSignal] User should disable notifications in browser settings')
  } catch (error) {
    console.error('[OneSignal] Failed to opt out:', error)
  }
}

/**
 * Get the current notification permission state
 */
export function getPermissionState(): NotificationPermission {
  if (!('Notification' in window)) return 'denied'
  return Notification.permission
}

/**
 * Check if push notifications are supported
 */
export function isPushSupported(): boolean {
  return 'serviceWorker' in navigator &&
         'PushManager' in window &&
         'Notification' in window &&
         isOneSignalConfigured()
}

/**
 * Add a listener for subscription changes
 */
export function onSubscriptionChange(callback: (isSubscribed: boolean) => void): void {
  if (!isInitialized) return

  OneSignal.Notifications.addEventListener('permissionChange', (permission) => {
    callback(permission)
  })
}

/**
 * Get OneSignal subscription ID (player ID)
 */
export async function getSubscriptionId(): Promise<string | null> {
  if (!isInitialized) return null

  try {
    const subscriptionId = OneSignal.User.PushSubscription.id
    return subscriptionId || null
  } catch (error) {
    console.error('[OneSignal] Failed to get subscription ID:', error)
    return null
  }
}
